import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type { GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import type {
  InformationSystemExecutiveSummary,
  RiskComplianceExecutiveSummary,
  RiskComplianceStandardRow,
} from '../../services/enterpriseOperationalUxApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import { formatPercentOrMissing } from '../../utils/operationalUx'
import {
  mapInformationSystemToUnifiedContext,
  mapStandardRowToUnifiedContext,
} from '../../utils/unifiedExecutiveContextMappers'
import ExecutiveOperationalContextPanel from './context/ExecutiveOperationalContextPanel'
import OperationalRuntimeTimeline from './context/OperationalRuntimeTimeline'
import OperationalBlastRadiusPanel from './context/OperationalBlastRadiusPanel'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

export type RiskComplianceSelection =
  | { kind: 'standard'; row: RiskComplianceStandardRow }
  | { kind: 'system'; systemId: string; grc?: GrcSummaryRow }
  | { kind: 'workflow'; workflow: RiskComplianceExecutiveSummary['unstableWorkflows'][number] }
  | { kind: 'runtime'; event: RiskComplianceExecutiveSummary['runtimeEvents'][number] }
  | { kind: 'blast'; impact: RiskComplianceExecutiveSummary['blastRadiusImpacts'][number] }
  | { kind: 'degradedControl'; controlCode: string; summary: string; impactedStandards?: string[] }
  | null

type Props = {
  selection: RiskComplianceSelection
  executiveBySystemId: Record<string, InformationSystemExecutiveSummary>
  onClose?: () => void
}

export default function RiskComplianceExecutiveContextPanel({
  selection,
  executiveBySystemId,
  onClose,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  if (!selection) {
    return (
      <div className={styles.rcContextEmpty}>
        <p>{t('enterpriseOperationalUx.riskCompliance.selectRisk', 'Select a standard, system, workflow, runtime event, or blast radius impact.')}</p>
      </div>
    )
  }

  const header = (title: string) => (
    <header className={styles.rcContextHeader}>
      <h3>{title}</h3>
      {onClose ? (
        <button type="button" className={styles.rcContextClose} onClick={onClose}>
          {t('common.close', 'Close')}
        </button>
      ) : null}
    </header>
  )

  if (selection.kind === 'standard') {
    const row = selection.row
    const context = mapStandardRowToUnifiedContext(row)
    return (
      <div className={styles.rcContextPanel}>
        {header(getStandardDisplayName(t, row.standardCode))}
        <dl className={entStyles.metricList}>
          <div className={entStyles.metricRow}>
            <dt>{t('standardGovernance.selfAssessment', 'Self-assessment')}</dt>
            <dd>{row.selfPercent != null ? formatPercentOrMissing(row.selfPercent, '') : '—'}</dd>
          </div>
          <div className={entStyles.metricRow}>
            <dt>{t('standardGovernance.verified', 'Verified')}</dt>
            <dd>{row.verifiedPercent != null ? formatPercentOrMissing(row.verifiedPercent, '') : '—'}</dd>
          </div>
        </dl>
        <ExecutiveOperationalContextPanel
          context={context}
          mode="compact"
          sections={{ recommendations: false }}
          footerActions={
            <button
              type="button"
              className={styles.rcWorkflowChip}
              onClick={() => navigate(`/compliance-workflow/${encodeURIComponent(row.standardCode)}`)}
            >
              {t('enterpriseOperationalUx.openWorkflow', 'Open workflow')}
            </button>
          }
        />
      </div>
    )
  }

  if (selection.kind === 'system') {
    const exec = executiveBySystemId[selection.systemId]
    const name = selection.grc?.name || selection.systemId
    if (!exec) {
      return (
        <div className={styles.rcContextPanel}>
          {header(name)}
          <p className={entStyles.emptyState}>{t('enterpriseOperationalUx.noExecutiveBaseline', 'No executive baseline established yet.')}</p>
        </div>
      )
    }
    const context = mapInformationSystemToUnifiedContext(exec, name)
    return (
      <div className={styles.rcContextPanel}>
        {header(name)}
        <ExecutiveOperationalContextPanel context={context} mode="compact" />
      </div>
    )
  }

  if (selection.kind === 'blast') {
    const imp = selection.impact
    return (
      <div className={styles.rcContextPanel}>
        {header(imp.controlCode)}
        <OperationalBlastRadiusPanel
          hits={[
            {
              controlCode: imp.controlCode,
              summary: imp.summary,
              impactedStandards: imp.impactedStandards || [],
              standardCount: imp.standardCount,
              systemCount: imp.systemCount,
              severity: 'MODERATE',
            },
          ]}
          mode="expanded"
        />
      </div>
    )
  }

  if (selection.kind === 'runtime') {
    const ev = selection.event
    return (
      <div className={styles.rcContextPanel}>
        {header(ev.eventType)}
        <OperationalRuntimeTimeline
          events={[
            {
              id: ev.id,
              at: ev.at,
              eventType: ev.eventType,
              severity: ev.severity || 'INFO',
              summary: ev.summary,
              relatedStandardCode: ev.relatedStandardCode,
            },
          ]}
          mode="full"
        />
      </div>
    )
  }

  if (selection.kind === 'workflow') {
    const w = selection.workflow
    return (
      <div className={styles.rcContextPanel}>
        {header(t('enterpriseOperationalUx.riskCompliance.unstableWorkflow', 'Unstable workflow'))}
        <p>{getStandardDisplayName(t, w.standardCode)} — {w.workflowStage}</p>
        <button
          type="button"
          className={styles.rcWorkflowChip}
          onClick={() => navigate(`/compliance-workflow/${encodeURIComponent(w.standardCode)}`)}
        >
          {t('enterpriseOperationalUx.openWorkflow', 'Open workflow')}
        </button>
      </div>
    )
  }

  if (selection.kind === 'degradedControl') {
    const dc = selection
    return (
      <div className={styles.rcContextPanel}>
        {header(dc.controlCode)}
        <OperationalBlastRadiusPanel
          hits={[
            {
              controlCode: dc.controlCode,
              summary: dc.summary,
              impactedStandards: dc.impactedStandards || [],
              standardCount: dc.impactedStandards?.length || 0,
              systemCount: 0,
              severity: 'MODERATE',
            },
          ]}
        />
      </div>
    )
  }

  return null
}
